/**
 * Framer Motion Animation Utilities for Lumora
 *
 * Centralized animation variants, easing functions, and configurations
 * for consistent, performance-optimized animations throughout the site.
 */

import type { Transition, Variants } from 'framer-motion';

// ============================================================================
// EASING FUNCTIONS
// ============================================================================

/**
 * Custom easing curves for fantasy-themed animations
 */
export const easings = {
  // Smooth, mystical feel for page transitions
  smooth: [0.4, 0, 0.2, 1] as [number, number, number, number],

  // Gentle ease for content reveals
  gentle: [0.25, 0.1, 0.25, 1] as [number, number, number, number],

  // Quick, responsive micro-interactions
  quick: [0.4, 0, 0.6, 1] as [number, number, number, number],

  // Bouncy, playful for hover states
  bounce: [0.68, -0.55, 0.265, 1.55] as [number, number, number, number],
};

/**
 * Spring configurations for physics-based animations
 */
export const springs = {
  // Gentle spring for card hovers
  gentle: {
    type: 'spring' as const,
    stiffness: 300,
    damping: 30,
  },

  // Snappy spring for buttons
  snappy: {
    type: 'spring' as const,
    stiffness: 400,
    damping: 25,
  },

  // Bouncy spring for playful interactions
  bouncy: {
    type: 'spring' as const,
    stiffness: 500,
    damping: 20,
  },

  // Smooth spring for modals
  smooth: {
    type: 'spring' as const,
    stiffness: 200,
    damping: 30,
  },
};

// ============================================================================
// TIMING CONSTANTS
// ============================================================================

export const durations = {
  fast: 0.2, // 200ms - Micro-interactions
  normal: 0.3, // 300ms - Standard transitions
  medium: 0.4, // 400ms - Modals, dropdowns
  slow: 0.5, // 500ms - Page transitions
  slower: 0.6, // 600ms - Scroll reveals
  slowest: 0.8, // 800ms - Complex animations
};

export const staggerDelays = {
  fast: 0.03, // 30ms - Dropdown items
  normal: 0.05, // 50ms - List rows
  medium: 0.1, // 100ms - Hero text cascade
  slow: 0.12, // 120ms - News cards
  slower: 0.15, // 150ms - Blog cards, feature grid
};

// ============================================================================
// PAGE TRANSITIONS
// ============================================================================

/**
 * Simple page fade - Main page transition pattern
 * Clean fade in/out without blur or scale
 */
export const pageTransition: Variants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: durations.normal,
      ease: easings.smooth,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: durations.fast,
      ease: easings.smooth,
    },
  },
};

/**
 * Instant page transition for rapid navigation
 */
export const pageTransitionFast: Variants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: durations.fast,
      ease: easings.smooth,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.1,
      ease: easings.smooth,
    },
  },
};

// ============================================================================
// SCROLL REVEAL ANIMATIONS
// ============================================================================

/**
 * Fade up - Content slides up and fades in (most common pattern)
 */
export const fadeUp: Variants = {
  hidden: {
    opacity: 0,
    y: 15,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: durations.slower,
      ease: easings.gentle,
    },
  },
};

/**
 * Fade down - Content slides down and fades in
 */
export const fadeDown: Variants = {
  hidden: {
    opacity: 0,
    y: -15,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: durations.slower,
      ease: easings.gentle,
    },
  },
};

/**
 * Fade left - Content slides from left and fades in
 */
export const fadeLeft: Variants = {
  hidden: {
    opacity: 0,
    x: -20,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: durations.slower,
      ease: easings.gentle,
    },
  },
};

/**
 * Fade right - Content slides from right and fades in
 */
export const fadeRight: Variants = {
  hidden: {
    opacity: 0,
    x: 20,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: durations.slower,
      ease: easings.gentle,
    },
  },
};

/**
 * Scale fade - Content scales up and fades in
 */
export const scaleFade: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: durations.slower,
      ease: easings.gentle,
    },
  },
};

/**
 * Zoom in - Content scales from small to normal
 */
export const zoomIn: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.8,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: durations.medium,
      ease: easings.gentle,
    },
  },
};

// ============================================================================
// STAGGER ANIMATIONS
// ============================================================================

/**
 * Stagger container for children animations
 * Use with staggerChildren delay
 */
export const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: staggerDelays.normal,
    },
  },
};

/**
 * Stagger container with fast delay (dropdown items)
 */
export const staggerContainerFast: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: staggerDelays.fast,
    },
  },
};

/**
 * Stagger container with slow delay (blog cards, feature grid)
 */
export const staggerContainerSlow: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: staggerDelays.slower,
    },
  },
};

/**
 * Stagger item - Use as child of stagger container
 */
export const staggerItem: Variants = {
  hidden: {
    opacity: 0,
    y: 10,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: durations.medium,
      ease: easings.gentle,
    },
  },
};

// ============================================================================
// CARD ANIMATIONS
// ============================================================================

/**
 * Card hover state - Lift and enhance shadow
 */
export const cardHover = {
  rest: {
    y: 0,
    scale: 1,
  },
  hover: {
    y: -4,
    scale: 1.01,
    transition: springs.gentle,
  },
  tap: {
    scale: 0.98,
    transition: springs.snappy,
  },
};

/**
 * Card entrance - Scale and fade in
 */
export const cardEntrance: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 10,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: durations.medium,
      ease: easings.gentle,
    },
  },
};

// ============================================================================
// MODAL/DIALOG ANIMATIONS
// ============================================================================

/**
 * Modal backdrop - Fade in with blur
 */
export const modalBackdrop: Variants = {
  hidden: {
    opacity: 0,
    backdropFilter: 'blur(0px)',
  },
  visible: {
    opacity: 1,
    backdropFilter: 'blur(8px)',
    transition: {
      duration: durations.medium,
      ease: easings.gentle,
    },
  },
  exit: {
    opacity: 0,
    backdropFilter: 'blur(0px)',
    transition: {
      duration: durations.normal,
      ease: easings.gentle,
    },
  },
};

/**
 * Modal content - Scale and fade in
 */
export const modalContent: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 10,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: springs.smooth,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 10,
    transition: {
      duration: durations.normal,
      ease: easings.gentle,
    },
  },
};

// ============================================================================
// NAVIGATION ANIMATIONS
// ============================================================================

/**
 * Mobile menu panel - Slide from right
 */
export const mobileMenu: Variants = {
  closed: {
    x: '100%',
    transition: {
      duration: durations.normal,
      ease: easings.smooth,
    },
  },
  open: {
    x: 0,
    transition: {
      duration: durations.medium,
      ease: easings.smooth,
    },
  },
};

/**
 * Dropdown content - Scale from top
 */
export const dropdown: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: -5,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: durations.fast,
      ease: easings.quick,
    },
  },
};

// ============================================================================
// MICRO-INTERACTIONS
// ============================================================================

/**
 * Button hover - Lift with glow
 */
export const buttonHover = {
  rest: {
    scale: 1,
    y: 0,
  },
  hover: {
    scale: 1.02,
    y: -2,
    transition: springs.snappy,
  },
  tap: {
    scale: 0.98,
    y: 0,
    transition: springs.snappy,
  },
};

/**
 * Icon rotation (chevron in accordion)
 */
export const iconRotate = {
  closed: {
    rotate: 0,
    transition: {
      duration: durations.normal,
      ease: easings.smooth,
    },
  },
  open: {
    rotate: 180,
    transition: {
      duration: durations.normal,
      ease: easings.smooth,
    },
  },
};

/**
 * Badge pulse - Gentle glow animation
 */
export const badgePulse: Variants = {
  initial: {
    opacity: 1,
    scale: 1,
  },
  pulse: {
    opacity: [1, 0.8, 1],
    scale: [1, 1.05, 1],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: easings.gentle,
    },
  },
};

// ============================================================================
// LOADING STATES
// ============================================================================

/**
 * Spinner rotation
 */
export const spinnerRotate = {
  animate: {
    rotate: 360,
    transition: {
      duration: 1,
      repeat: Infinity,
      ease: 'linear',
    },
  },
};

/**
 * Skeleton shimmer effect
 */
export const skeletonShimmer: Variants = {
  initial: {
    x: '-100%',
  },
  animate: {
    x: '100%',
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

/**
 * Pulse loading
 */
export const pulseLoading: Variants = {
  initial: {
    opacity: 0.5,
  },
  animate: {
    opacity: [0.5, 1, 0.5],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: easings.gentle,
    },
  },
};

// ============================================================================
// HERO SECTION ANIMATIONS
// ============================================================================

/**
 * Hero text cascade - For title, subtitle, CTA sequence
 */
export const heroCascade: Variants = {
  hidden: {
    opacity: 0,
    y: 15,
  },
  visible: (custom: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: durations.slower,
      delay: custom * staggerDelays.medium,
      ease: easings.gentle,
    },
  }),
};

/**
 * Accent border animation - Slides in from left
 */
export const accentBorderSlide: Variants = {
  hidden: {
    scaleX: 0,
    originX: 0,
  },
  visible: {
    scaleX: 1,
    transition: {
      duration: durations.slowest,
      ease: easings.smooth,
    },
  },
};

// ============================================================================
// LIST & GRID ANIMATIONS
// ============================================================================

/**
 * Blog card stagger (horizontal layout)
 */
export const blogCardStagger = (index: number) => ({
  hidden: {
    opacity: 0,
    x: -10,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: durations.medium,
      delay: index * staggerDelays.slower,
      ease: easings.gentle,
    },
  },
});

/**
 * Feature grid stagger (vertical reveal)
 */
export const featureGridStagger = (index: number) => ({
  hidden: {
    opacity: 0,
    y: 15,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: durations.medium,
      delay: index * staggerDelays.slower,
      ease: easings.gentle,
    },
  },
});

/**
 * Database card grid stagger (row-by-row)
 */
export const databaseGridStagger = (index: number, columns: number = 3) => {
  const row = Math.floor(index / columns);
  const col = index % columns;

  return {
    hidden: {
      opacity: 0,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: durations.medium,
        delay: row * staggerDelays.medium + col * staggerDelays.fast,
        ease: easings.gentle,
      },
    },
  };
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get transition config with optional overrides
 */
export function getTransition(
  duration: number = durations.normal,
  easing: typeof easings[keyof typeof easings] = easings.smooth,
  delay: number = 0
): Transition {
  return {
    duration,
    ease: easing,
    delay,
  };
}

/**
 * Create custom stagger container with specified delay
 */
export function createStaggerContainer(delay: number): Variants {
  return {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: delay,
      },
    },
  };
}

/**
 * Viewport configuration for scroll reveals
 * Triggers when element is 20% visible
 */
export const scrollViewport = {
  once: true, // Only animate once
  amount: 0.2, // Trigger when 20% visible
  margin: '0px 0px -100px 0px', // Start slightly before element enters viewport
};

/**
 * Viewport configuration for immediate reveals
 * Triggers as soon as any part is visible
 */
export const scrollViewportImmediate = {
  once: true,
  amount: 0.1, // Trigger when 10% visible
};
